import * as stylex from '@stylexjs/stylex'
import {
  cloneElement,
  useContext,
  useRef,
  useState,
  type ComponentPropsWithRef,
  type ReactElement,
  type ReactNode,
} from 'react'
import { breakpoints, colors, fonts, fontSizes, radii, spacing, sweep } from '../tokens/tokens.stylex'
import { LinkRendererContext } from './link-provider'

const styles = stylex.create({
  host: {
    position: 'relative',
    height: sweep.height,
    flexShrink: 0,
    alignItems: 'center',
    overflow: 'hidden',
    borderRadius: radii.small,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: sweep.border,
    paddingInline: spacing.space16,
    paddingBlock: sweep.paddingBlock,
    '--sweep-scale': '0',
    '--sweep-brightness': '1',
  },
  block: { display: 'flex' },
  inline: { display: 'inline-flex' },
  live: { '--sweep-scale': { default: '0', ':hover': '1' }, '--sweep-brightness': { default: '1', ':active': '0.95' } },
  quiet: {
    backgroundColor: { default: 'transparent', ':hover': sweep.quiet },
    transitionProperty: 'background-color',
    transitionDuration: { default: sweep.duration, [breakpoints.reducedMotion]: '0s' },
  },
  control: { cursor: 'pointer' },
  disabled: { cursor: 'not-allowed', opacity: 0.5 },
  face: { position: 'relative', display: 'flex', alignItems: 'center', gap: spacing.space8 },
  text: {
    fontFamily: fonts.marketingMono,
    fontSize: fontSizes.small,
    lineHeight: 'normal',
    fontWeight: 400,
    whiteSpace: 'nowrap',
    color: colors.marketingForeground,
  },
  fill: {
    position: 'absolute',
    inset: 0,
    transformOrigin: 'left',
    transform: 'scaleX(var(--sweep-scale))',
    filter: 'brightness(var(--sweep-brightness))',
    transitionProperty: 'transform',
    transitionDuration: sweep.duration,
    transitionTimingFunction: sweep.easing,
  },
  quick: { transitionDuration: sweep.quickDuration },
  fillColor: (color: string) => ({ backgroundColor: color }),
  ring: {
    outlineWidth: { default: null, ':focus-visible': 2 },
    outlineStyle: { default: null, ':focus-visible': 'solid' },
    outlineOffset: { default: null, ':focus-visible': 2 },
    outlineColor: { default: null, ':focus-visible': colors.marketingForeground },
  },
  pickerRing: {
    outlineWidth: { default: null, ':has(:focus-visible)': 2 },
    outlineStyle: { default: null, ':has(:focus-visible)': 'solid' },
    outlineOffset: { default: null, ':has(:focus-visible)': 2 },
    outlineColor: { default: null, ':has(:focus-visible)': colors.marketingForeground },
  },
  start: { alignSelf: 'flex-start' },
  spaced: { marginTop: spacing.space8 },
})

const fills = [sweep.pink, sweep.yellow, sweep.cyan, sweep.mint] as const

function useSweep() {
  const previous = useRef(-1)
  const [color, setColor] = useState<string>(sweep.yellow)

  const recolor = () => {
    let next = Math.floor(Math.random() * fills.length)

    if (next === previous.current) next = (next + 1 + Math.floor(Math.random() * (fills.length - 1))) % fills.length

    previous.current = next

    switch (next) {
      case 0:
        setColor(sweep.pink)

        break
      case 1:
        setColor(sweep.yellow)

        break
      case 2:
        setColor(sweep.cyan)

        break
      default:
        setColor(sweep.mint)
    }
  }

  return { color, recolor }
}

type SweepOptions = {
  quiet?: boolean
  focus?: 'native' | 'ring'
  placement?: 'flow' | 'start' | 'start-spaced'
}

type NativeAnchor = Omit<ComponentPropsWithRef<'a'>, 'className' | 'style' | 'color'>

type SweepLinkOptions = {
  render?: ReactElement<NativeAnchor> | undefined
  layout?: 'block' | 'inline'
  typography?: 'content' | 'mono'
  duration?: 'standard' | 'quick'
}

type SweepLinkProps = NativeAnchor & SweepOptions & SweepLinkOptions
type SweepButtonProps = Omit<ComponentPropsWithRef<'button'>, 'className' | 'style' | 'color'> & SweepOptions
type SweepLabelProps = Omit<ComponentPropsWithRef<'label'>, 'className' | 'style' | 'color'> & SweepOptions

type SweepFaceProps = { color: string; quiet: boolean; mono: boolean; quick?: boolean; children: ReactNode }

function SweepFace({ color, quiet, mono, quick, children }: SweepFaceProps) {
  return (
    <>
      {!quiet && (
        <span aria-hidden="true" {...stylex.props(styles.fill, styles.fillColor(color), quick && styles.quick)} />
      )}
      <span {...stylex.props(styles.face, mono && styles.text)}>{children}</span>
    </>
  )
}

/** Use for an outlined destination with a paint sweep. Choose ButtonLink for ordinary navigation. Applications supply the destination and optional router anchor. Status: supported. */
export function SweepLink({
  quiet = false,
  focus = 'native',
  placement = 'flow',
  render,
  layout = 'block',
  typography = 'content',
  duration = 'standard',
  children,
  onPointerEnter,
  ...props
}: SweepLinkProps) {
  const { color, recolor } = useSweep()
  const renderLink = useContext(LinkRendererContext)

  const anchorProps = {
    ...props,
    onPointerEnter: (event: React.PointerEvent<HTMLAnchorElement>) => {
      if (!quiet) recolor()

      onPointerEnter?.(event)
    },
    ...stylex.props(
      styles.host,
      styles[layout],
      quiet ? styles.quiet : styles.live,
      focus === 'ring' && styles.ring,
      placement !== 'flow' && styles.start,
      placement === 'start-spaced' && styles.spaced
    ),
  }

  const content = (
    <SweepFace color={color} quiet={quiet} mono={typography === 'mono'} quick={duration === 'quick'}>
      {children}
    </SweepFace>
  )

  if (render) return cloneElement(render, anchorProps, content)

  if (renderLink) return renderLink({ ...anchorProps, children: content })

  return <a {...anchorProps}>{content}</a>
}

/** Use for an action matching the outlined sweep links. Choose Button for standard actions. Applications own disabled state and submission. Status: supported. */
export function SweepButton({
  quiet = false,
  focus = 'native',
  placement = 'flow',
  children,
  disabled,
  onPointerEnter,
  type = 'button',
  ...props
}: SweepButtonProps) {
  const { color, recolor } = useSweep()
  const live = !disabled

  return (
    <button
      {...props}
      type={type}
      disabled={disabled}
      onPointerEnter={(event) => {
        if (live && !quiet) recolor()

        onPointerEnter?.(event)
      }}
      {...stylex.props(
        styles.host,
        styles.inline,
        styles.control,
        !live && styles.disabled,
        live && (quiet ? styles.quiet : styles.live),
        focus === 'ring' && styles.ring,
        placement !== 'flow' && styles.start,
        placement === 'start-spaced' && styles.spaced
      )}
    >
      <SweepFace color={color} quiet={quiet} mono>
        {children}
      </SweepFace>
    </button>
  )
}

/** Use for a labeled native file input with the sweep appearance. The application supplies the input and its disabled state. Choose SweepButton for an action. Status: supported. */
export function SweepLabel({
  quiet = false,
  focus = 'native',
  placement = 'flow',
  children,
  onPointerEnter,
  ...props
}: SweepLabelProps) {
  const { color, recolor } = useSweep()
  const live = !(props['aria-disabled'] === true || props['aria-disabled'] === 'true')

  return (
    <label
      {...props}
      onPointerEnter={(event) => {
        if (live && !quiet) recolor()

        onPointerEnter?.(event)
      }}
      {...stylex.props(
        styles.host,
        styles.inline,
        styles.control,
        !live && styles.disabled,
        live && (quiet ? styles.quiet : styles.live),
        focus === 'ring' && styles.pickerRing,
        placement !== 'flow' && styles.start,
        placement === 'start-spaced' && styles.spaced
      )}
    >
      <SweepFace color={color} quiet={quiet} mono>
        {children}
      </SweepFace>
    </label>
  )
}
